import React from 'react';
import {
  Grid,
  GridColumn,
  GridRow,
  CondensedLabel,
  CondensedMedium,
  Gutter,
  useApi,
  DateUtil,
  PositionUtil,
} from '@axiom/ui';
import type { CandidateWorkFeedItem } from '@axiom/validation';

import { TaxonomyApi } from '../../api/taxonomy-api';
import { LocationUtil } from '../../utils/location-util';
import { WorkFeedUtil } from '../../utils/WorkFeedUtil';

export type WorkFeedCardDataPointsProps = {
  workFeedItem: CandidateWorkFeedItem;
  index: number;
  name?: string;
};

export const WorkFeedCardDataPoints = ({
  workFeedItem,
  index,
  name = 'WORK_FEED_CARD_DATA_POINTS',
}: WorkFeedCardDataPointsProps) => {
  const [{ data: taxonomy }] = useApi(TaxonomyApi.readTaxonomy());

  const languagesDisplay = WorkFeedUtil.getLanguagesDisplay(
    workFeedItem.opportunity
  );
  const shouldShowLanguages =
    !WorkFeedUtil.isUSBased(workFeedItem.opportunity) &&
    languagesDisplay.length > 0;

  return (
    <Gutter bottom="16px" name={name}>
      <Grid stackable name="PROJECT_DETAILS_CONTENT">
        <GridRow>
          <GridColumn width={6}>
            <Grid>
              <GridRow>
                <GridColumn
                  largeScreenWidth={6}
                  tabletWidth={6}
                  mobileWidth={12}
                >
                  <CondensedLabel>Estimated Start Date</CondensedLabel>
                </GridColumn>
                <GridColumn
                  largeScreenWidth={4}
                  tabletWidth={4}
                  mobileWidth={12}
                >
                  <CondensedMedium name="ESTIMATED_START_DATE">
                    {DateUtil.displayDate(workFeedItem.position.startDate) ??
                      '--'}
                  </CondensedMedium>
                  <Gutter only="mobile" bottom="8px" />
                </GridColumn>
                <GridColumn
                  largeScreenWidth={6}
                  tabletWidth={6}
                  mobileWidth={12}
                >
                  <CondensedLabel>Estimated Length</CondensedLabel>
                </GridColumn>
                <GridColumn
                  largeScreenWidth={4}
                  tabletWidth={4}
                  mobileWidth={12}
                >
                  <CondensedMedium name="ESTIMATED_LENGTH">
                    {PositionUtil.getEngagementLengthGrouped(
                      workFeedItem.position
                    ) ?? '--'}
                  </CondensedMedium>
                  <Gutter only="mobile" bottom="8px" />
                </GridColumn>
                <GridColumn
                  largeScreenWidth={6}
                  tabletWidth={6}
                  mobileWidth={12}
                >
                  <CondensedLabel>Weekly Commitment</CondensedLabel>
                </GridColumn>
                <GridColumn
                  largeScreenWidth={4}
                  tabletWidth={4}
                  mobileWidth={12}
                >
                  <CondensedMedium name="WEEKLY_COMMITMENT">
                    {PositionUtil.getHoursPerWeek(workFeedItem.position) ??
                      '--'}
                  </CondensedMedium>
                  <Gutter only="mobile" bottom="8px" />
                </GridColumn>
                {shouldShowLanguages ? (
                  <>
                    <GridColumn
                      largeScreenWidth={6}
                      tabletWidth={6}
                      mobileWidth={12}
                    >
                      <CondensedLabel>Language</CondensedLabel>
                    </GridColumn>
                    <GridColumn
                      largeScreenWidth={4}
                      tabletWidth={4}
                      mobileWidth={12}
                    >
                      <CondensedMedium name="LANGUAGE">
                        {languagesDisplay}
                      </CondensedMedium>
                    </GridColumn>
                  </>
                ) : null}
              </GridRow>
            </Grid>
          </GridColumn>
          <GridColumn width={6}>
            <Grid>
              <GridRow>
                <GridColumn
                  largeScreenWidth={4}
                  tabletWidth={4}
                  mobileWidth={12}
                >
                  <CondensedLabel>Location</CondensedLabel>
                </GridColumn>
                <GridColumn
                  largeScreenWidth={8}
                  tabletWidth={8}
                  mobileWidth={12}
                >
                  <CondensedMedium name="LOCATION">
                    {LocationUtil.displayFormattedJobLocation(
                      workFeedItem.opportunity,
                      workFeedItem.position
                    )}
                  </CondensedMedium>
                  <Gutter only="mobile" bottom="8px" />
                </GridColumn>
                <GridColumn
                  largeScreenWidth={4}
                  tabletWidth={4}
                  mobileWidth={12}
                >
                  <CondensedLabel>Practice Area</CondensedLabel>
                </GridColumn>
                <GridColumn
                  largeScreenWidth={8}
                  tabletWidth={8}
                  mobileWidth={12}
                >
                  <CondensedMedium name="PRACTICE_AREA">
                    {taxonomy.practiceAreas[
                      workFeedItem.opportunity.primaryPracticeArea ?? ''
                    ]?.fullName || '--'}
                  </CondensedMedium>
                  <Gutter only="mobile" bottom="8px" />
                </GridColumn>
                <GridColumn
                  largeScreenWidth={4}
                  tabletWidth={4}
                  mobileWidth={12}
                >
                  <CondensedLabel>Focus Area</CondensedLabel>
                </GridColumn>
                <GridColumn
                  largeScreenWidth={8}
                  tabletWidth={8}
                  mobileWidth={12}
                >
                  <CondensedMedium name="FOCUS_AREA">
                    {taxonomy.focusAreas[
                      workFeedItem.opportunity.primaryFocusArea ?? ''
                    ]?.fullName || '--'}
                  </CondensedMedium>
                  <Gutter only="mobile" bottom="8px" />
                </GridColumn>
                <GridColumn
                  largeScreenWidth={4}
                  tabletWidth={4}
                  mobileWidth={12}
                >
                  <CondensedLabel
                    name={index > 0 ? 'TALENT_TYPE' : 'TALENT_TYPE_FIRST_CARD'}
                  >
                    Legal Role
                  </CondensedLabel>
                </GridColumn>
                <GridColumn
                  largeScreenWidth={8}
                  tabletWidth={8}
                  mobileWidth={12}
                >
                  <CondensedMedium name="LEGAL_ROLE">
                    {taxonomy.businessTitles[
                      workFeedItem.position.businessTitle ?? ''
                    ]?.displayName || '--'}
                  </CondensedMedium>
                </GridColumn>
              </GridRow>
            </Grid>
          </GridColumn>
        </GridRow>
      </Grid>
    </Gutter>
  );
};
